// src/pages/Search.tsx
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Outlet } from "react-router-dom";
import { Box, Fab, Typography } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// Hooks
import { useSearchFilters } from "@/search/useSearchFilters";
import { useAddressSearch } from "@/search/useAddressSearch";
import {
  useRecentSearches,
  useInvalidateRecentSearchesOnFetch,
} from "@/search/useRecentSearches";

// Components
import SearchForm from "@/components/search/SearchForm";
import SearchFacets from "@/components/search/SearchFacets";
import RecentSearchesList from "@/components/search/RecentSearchesList";
import AddressResultsList from "@/components/search/AddressResultsList";

// Types
import type { StreetResult } from "./StreetAutocomplete";
import type { AddressResult, RecentSearch } from "@/search/types";
import { parseSearchParams } from "@/search/utils";

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ─────────────────────────────────────────────────────────────────
  // Search Filters (URL params + form state)
  // ─────────────────────────────────────────────────────────────────
  const {
    filters,
    hasParams,
    anyFacetActive,
    formState,
    setStreetInput,
    setPostcodeInput,
    setStatusInput,
    isFormPristine,
    handleSearch: triggerSearch,
    handleClear,
    updateFacets,
    clearAllFacets,
    applyFilters,
  } = useSearchFilters();

  // ─────────────────────────────────────────────────────────────────
  // Address Search (infinite query + location tracking)
  // ─────────────────────────────────────────────────────────────────
  const {
    data: facetPage,
    allResults,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetched,
    hasNextPage,
    loadMoreRef,
    searchRequestId,
    beginSearchLocationFlow,
    clearSearchLocationFlow,
    primeAddressDetail,
    online,
  } = useAddressSearch({ filters, hasParams });

  // ─────────────────────────────────────────────────────────────────
  // Recent Searches
  // ─────────────────────────────────────────────────────────────────
  const {
    recentSearches,
    isLoading: isHistoryLoading,
    isError: isHistoryError,
    isFetching: isHistoryFetching,
    hasData: historyHasData,
    pinSearch,
    unpinSearch,
    expandedIds,
    toggleExpanded,
  } = useRecentSearches();

  // Invalidate recent searches after a search completes
  useInvalidateRecentSearchesOnFetch(isFetched, hasParams);

  // ─────────────────────────────────────────────────────────────────
  // Scroll to Top
  // ─────────────────────────────────────────────────────────────────
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ─────────────────────────────────────────────────────────────────
  // Facet Page Caching (keep facets visible while loading new results)
  // ─────────────────────────────────────────────────────────────────
  const baseSearchKey = `${filters.street}||${filters.postcode}||${filters.statusList.join(",")}`;
  const baseKeyRef = useRef(baseSearchKey);
  const [cachedFacetPage, setCachedFacetPage] = useState(facetPage);

  useEffect(() => {
    if (baseSearchKey !== baseKeyRef.current) {
      setCachedFacetPage(null);
      baseKeyRef.current = baseSearchKey;
    }
  }, [baseSearchKey]);

  useEffect(() => {
    if (facetPage) {
      setCachedFacetPage(facetPage);
    }
  }, [facetPage]);

  const displayFacetPage = facetPage ?? cachedFacetPage;

  // ─────────────────────────────────────────────────────────────────
  // Auto-trigger search on mount/params change
  // ─────────────────────────────────────────────────────────────────
  const lastParamsRef = useRef<string>("");

  useEffect(() => {
    // Only trigger if we have params but no active search
    if (!hasParams) return;

    // Check if params have changed (avoid duplicate triggers)
    const currentParams = searchParams.toString();
    if (currentParams === lastParamsRef.current) return;

    // Only trigger if there's no active searchRequestId
    // (This prevents duplicate triggers if beginSearchLocationFlow was already called)
    if (searchRequestId) return;

    lastParamsRef.current = currentParams;
    beginSearchLocationFlow();
  }, [hasParams, searchParams, searchRequestId, beginSearchLocationFlow]);

  // ─────────────────────────────────────────────────────────────────
  // Event Handlers
  // ─────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(() => {
    beginSearchLocationFlow();
    triggerSearch();
  }, [beginSearchLocationFlow, triggerSearch]);

  const handleClearAll = useCallback(() => {
    handleClear();
    clearSearchLocationFlow();
  }, [handleClear, clearSearchLocationFlow]);

  const handleStreetSelect = useCallback(
    (street: StreetResult | null) => {
      if (!street) {
        handleClearAll();
        return;
      }

      const nextStreet = street.thoroughfare || "";
      const nextPostcode = street.postcodes.join(", ");

      beginSearchLocationFlow();
      applyFilters(nextStreet, nextPostcode, formState.statusInput);
    },
    [beginSearchLocationFlow, applyFilters, formState.statusInput, handleClearAll]
  );

  const handleSelectRecentSearch = useCallback(
    (entry: RecentSearch) => {
      const { street, postcodes, statuses } = parseSearchParams(entry.params);
      const postcodeStr = postcodes.join(", ");

      setStreetInput(street);
      setPostcodeInput(postcodeStr);
      setStatusInput(statuses);

      beginSearchLocationFlow();
      applyFilters(street, postcodeStr, statuses);
    },
    [
      setStreetInput,
      setPostcodeInput,
      setStatusInput,
      beginSearchLocationFlow,
      applyFilters,
    ]
  );

  const handleSelectAddress = useCallback(
    (addr: AddressResult) => {
      primeAddressDetail(addr);
      navigate({
        pathname: `address/${addr.udprn}`,
        search: searchParams.toString(),
      });
    },
    [primeAddressDetail, navigate, searchParams]
  );

  // ─────────────────────────────────────────────────────────────────
  // Derived State
  // ─────────────────────────────────────────────────────────────────
  const showHistory = !hasParams && isFormPristine;
  const showHistorySkeleton = (isHistoryLoading || isHistoryFetching) && !historyHasData;
  const isInitialLoading = isLoading && allResults.length === 0;
  // Keep count visible during background refetches when we already have data
  const hideResultCount =
    isInitialLoading || (isFetching && !isFetchingNextPage && allResults.length === 0);

  const totalResultsCount = displayFacetPage?.pre_facet_count;
  const filteredResultsCount =
    typeof facetPage?.count === "number"
      ? facetPage.count
      : typeof displayFacetPage?.count === "number"
        ? displayFacetPage.count
        : allResults.length;

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 0, background: "#f6f6f6", minHeight: "100%" }}>
      {/* Search Form */}
      <SearchForm
        streetInput={formState.streetInput}
        postcodeInput={formState.postcodeInput}
        statusInput={formState.statusInput}
        onStreetChange={setStreetInput}
        onPostcodeChange={setPostcodeInput}
        onStatusChange={setStatusInput}
        onStreetSelect={handleStreetSelect}
        onSearch={handleSearch}
        onClear={handleClearAll}
        enableAutocomplete={online}
      />

      {/* Facet Filters */}
      <SearchFacets
        facetPage={displayFacetPage}
        postcodeFacet={filters.postcodeFacet}
        cityFacet={filters.cityFacet}
        localityFacet={filters.localityFacet}
        statusFacet={filters.statusFacet}
        anyFacetActive={anyFacetActive}
        isLoading={isInitialLoading}
        onPostcodeFacetChange={(pcs) => updateFacets({ postcode_facet: pcs })}
        onCityFacetChange={(towns) => updateFacets({ post_town_facet: towns })}
        onLocalityFacetChange={(locs) => updateFacets({ dependent_locality_facet: locs })}
        onStatusFacetChange={(sts) => updateFacets({ status_facet: sts })}
        onClearAllFacets={clearAllFacets}
      />

      {/* Recent Searches */}
      {showHistory && !isHistoryError && (showHistorySkeleton || historyHasData) && (
        <RecentSearchesList
          searches={recentSearches}
          isLoading={isHistoryLoading || isHistoryFetching}
          hasData={historyHasData}
          expandedIds={expandedIds}
          onToggleExpanded={toggleExpanded}
          onSelectSearch={handleSelectRecentSearch}
          onPinSearch={pinSearch}
          onUnpinSearch={unpinSearch}
        />
      )}

      {/* Result Count */}
      {!hideResultCount && typeof totalResultsCount === "number" && displayFacetPage && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1 }}>
          {!anyFacetActive
            ? `${totalResultsCount} results found`
            : `Showing ${filteredResultsCount} results from ${totalResultsCount}`}
        </Typography>
      )}

      {/* Offline Indicator */}
      {facetPage?.offline && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ px: 2, mb: 0.5, fontWeight: 600 }}
        >
          Offline Results!
        </Typography>
      )}

      {/* Results List */}
      <AddressResultsList
        results={allResults}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        loadMoreRef={loadMoreRef}
        onSelectAddress={handleSelectAddress}
      />

      {/* No Results */}
      {facetPage && !isLoading && allResults.length === 0 && (
        <Box textAlign="center" py={3}>
          <Typography color="text.secondary">
            No addresses match your filters.
          </Typography>
        </Box>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <Fab
          color="primary"
          size="small"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          sx={{
            position: "fixed",
            bottom: 90,
            right: 16,
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      <Outlet />
    </Box>
  );
}
